import { getEquityUsedCount } from '@/api/activity'
import { useUserStore } from '@/stores/user'
import {
  getServiceCaseType,
  formatServiceBtnText,
  normalizeBillingType,
  saveRescueEquityInfo
} from '@/utils/equityCaseType'

/**
 * 权益剩余次数 mixin
 * 使用方需在组件选项中声明 `equityServiceKey`（如 urgent、medicalReturn）
 */
export default {
  data() {
    return {
      /** @type {Object|null} 权益使用统计接口 data */
      equityUsedInfo: null,
      /** 解析后的 productId（路由优先，其次权益详情） */
      resolvedProductId: null,
      /** 是否正在拉取剩余次数 */
      equityUsedLoading: false
    }
  },
  computed: {
    /**
     * 当前服务对应的 caseType
     * @returns {string}
     */
    currentCaseType() {
      const serviceKey = this.$options.equityServiceKey
      const productId =
        this.resolvedProductId != null && this.resolvedProductId !== ''
          ? this.resolvedProductId
          : this.$route.query.productId
      return getServiceCaseType(productId, serviceKey)
    },
    /**
     * 获取服务按钮文案（含剩余次数）
     * @returns {string}
     */
    getServiceBtnText() {
      return formatServiceBtnText(this.equityUsedInfo, '获取服务')
    },
    /**
     * 是否还可继续使用该权益
     * @returns {boolean}
     */
    equityCanUse() {
      if (!this.equityUsedInfo) return true
      return this.equityUsedInfo.canUse !== false
    }
  },
  created() {
    this.fetchEquityUsedCount()
  },
  methods: {
    /**
     * 解析 productId：路由 query > 权益详情接口
     * @returns {Promise<string|number|null>}
     */
    async resolveProductId() {
      const fromRoute = this.$route.query.productId
      if (fromRoute != null && String(fromRoute).trim() !== '') {
        return fromRoute
      }
      try {
        const userStore = useUserStore()
        const res = await userStore.queryHealthQuanyi()
        if (res && (res.code === '1000' || res.code === 1000) && res.data && res.data.length > 0) {
          const productId = res.data[0].productId
          if (productId != null && productId !== '') {
            return productId
          }
        }
      } catch (e) {
        console.error('获取权益详情 productId 失败:', e)
      }
      return null
    },
    /**
     * 拉取权益使用统计并更新按钮文案
     * @returns {Promise<void>}
     */
    async fetchEquityUsedCount() {
      this.equityUsedLoading = true
      try {
        const productId = await this.resolveProductId()
        this.resolvedProductId = productId
        const serviceKey = this.$options.equityServiceKey
        const caseType = getServiceCaseType(productId, serviceKey)

        if (productId == null || productId === '' || !caseType) {
          console.warn('[equityUsedCount] 缺少 productId 或 caseType，跳过查询', {
            productId,
            caseType,
            serviceKey
          })
          this.equityUsedInfo = null
          return
        }

        const res = await getEquityUsedCount({
          productId,
          caseType
        })
        console.log('[equityUsedCount] 查询结果', { productId, caseType, res })

        if (res && (res.code === '1000' || res.code === 1000) && res.data) {
          const data = res.data
          // 统一数值字段，避免字符串导致展示判断失败
          this.equityUsedInfo = {
            ...data,
            billingType: normalizeBillingType(data.billingType),
            usedCount: data.usedCount != null ? Number(data.usedCount) : 0,
            timesCount:
              data.timesCount != null && data.timesCount !== ''
                ? Number(data.timesCount)
                : null
          }
        } else {
          this.equityUsedInfo = null
        }
      } catch (e) {
        console.error('查询权益使用统计失败:', e)
        this.equityUsedInfo = null
      } finally {
        this.equityUsedLoading = false
      }
    },
    /**
     * 校验剩余次数后跳转一键救援（必须携带本二级页对应的 caseType）
     * @returns {boolean} 是否已发起跳转
     */
    goRescueWithCaseType() {
      if (!this.equityCanUse) {
        const tip = '该权益可用次数已用完'
        if (this.$toast) {
          this.$toast(tip)
        } else if (this.$message) {
          this.$message.warning(tip)
        } else {
          window.alert(tip)
        }
        return false
      }
      const caseType = this.currentCaseType
      if (!caseType) {
        console.error('[goRescueWithCaseType] 未解析到 caseType', {
          serviceKey: this.$options.equityServiceKey,
          productId: this.resolvedProductId || this.$route.query.productId
        })
        const tip = '服务类型异常，请稍后重试'
        if (this.$toast) {
          this.$toast(tip)
        } else if (this.$message) {
          this.$message.error(tip)
        }
        return false
      }
      const query = {
        caseType,
        // 标记来自二级服务页，便于一键救援页区分入口
        from: 'service'
      }
      const productId =
        this.resolvedProductId != null && this.resolvedProductId !== ''
          ? this.resolvedProductId
          : this.$route.query.productId
      if (productId != null && productId !== '') {
        query.productId = productId
      }
      // 暂存权益统计，供一键救援接口透传（与 equity-used-count 响应对齐）
      saveRescueEquityInfo(this.equityUsedInfo)
      this.$router.push({
        name: 'onClickRescue',
        query
      })
      return true
    },
    /**
     * 校验剩余次数；不可用时提示并返回 false
     * @returns {boolean}
     */
    ensureEquityCanUse() {
      if (this.equityCanUse) return true
      const tip = '该权益可用次数已用完'
      if (this.$toast) {
        this.$toast(tip)
      } else if (this.$message) {
        this.$message.warning(tip)
      } else {
        window.alert(tip)
      }
      return false
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/equityUsedCountMixin.js